import React, { useEffect, useMemo, useRef } from 'react';
import { LineSegmentStatus, PnidNode, PnidNodeLineSegment } from '../types';
import { mergeOverlappingLineSegments, MergedLineSegment } from '../utils/geometry';

interface LineSegmentReviewPopoverProps {
  node: PnidNode;
  position: { x: number; y: number };
  hoveredDistinctId: string | null;
  selectedDistinctId: string | null;
  segmentCoordinateInfo?: {
    sx: number;
    sy: number;
    source?: { width_px: number; height_px: number; render_scale?: number };
    source_label?: string;
    target?: { width_px: number; height_px: number };
  } | null;
  onHoverDistinct: (distinctId: string | null) => void;
  onSelectDistinct: (distinctId: string | null) => void;
  onConfirmDistinct: (distinct: MergedLineSegment) => void;
  onDeleteDistinct: (distinct: MergedLineSegment) => void;
  onConfirmAll: () => void;
  onClose: () => void;
}

function deriveDistinctStatus(cluster: PnidNodeLineSegment[]): LineSegmentStatus {
  if (cluster.length === 0) return 'pending';
  if (cluster.every((s) => s.status === 'deleted')) return 'deleted';
  if (cluster.some((s) => s.status === 'edited')) return 'edited';
  if (cluster.some((s) => s.status === 'pending')) return 'pending';
  if (cluster.every((s) => s.status === 'confirmed' || s.status === 'deleted')) return 'confirmed';
  return 'pending';
}

function statusStyle(status: LineSegmentStatus): { color: string; bg: string; border: string; label: string } {
  switch (status) {
    case 'confirmed':
      return { color: '#047857', bg: '#d1fae5', border: '#a7f3d0', label: 'Confirmed' };
    case 'edited':
      return { color: '#92400e', bg: '#ffedd5', border: '#fed7aa', label: 'Edited' };
    case 'deleted':
      return { color: '#b91c1c', bg: '#fee2e2', border: '#fecaca', label: 'Deleted' };
    case 'pending':
    default:
      return { color: '#374151', bg: '#f3f4f6', border: '#e5e7eb', label: 'Pending' };
  }
}

function SegmentPreview({ seg, status }: { seg: MergedLineSegment; status: LineSegmentStatus }) {
  const { color } = statusStyle(status);
  const w = 70;
  const h = 18;
  const pad = 2;
  const xMin = Math.min(seg.x1, seg.x2);
  const xMax = Math.max(seg.x1, seg.x2);
  const yMin = Math.min(seg.y1, seg.y2);
  const yMax = Math.max(seg.y1, seg.y2);
  const dx = Math.max(1e-6, xMax - xMin);
  const dy = Math.max(1e-6, yMax - yMin);
  const sx = (w - pad * 2) / dx;
  const sy = (h - pad * 2) / dy;
  const s = Math.min(sx, sy);
  const cx = (xMin + xMax) / 2;
  const cy = (yMin + yMax) / 2;
  const map = (x: number, y: number) => ({
    x: w / 2 + (x - cx) * s,
    y: h / 2 + (y - cy) * s,
  });
  const p1 = map(seg.x1, seg.y1);
  const p2 = map(seg.x2, seg.y2);

  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={color} strokeWidth={2} strokeLinecap="round" />
      <circle cx={p1.x} cy={p1.y} r={2.5} fill={color} />
      <circle cx={p2.x} cy={p2.y} r={2.5} fill={color} />
    </svg>
  );
}

const LineSegmentReviewPopover: React.FC<LineSegmentReviewPopoverProps> = ({
  node,
  position,
  hoveredDistinctId,
  selectedDistinctId,
  segmentCoordinateInfo,
  onHoverDistinct,
  onSelectDistinct,
  onConfirmDistinct,
  onDeleteDistinct,
  onConfirmAll,
  onClose,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const rawSegments = node.line_segments ?? [];
  const sx = segmentCoordinateInfo?.sx ?? 1;
  const sy = segmentCoordinateInfo?.sy ?? 1;

  const distinctSegments = useMemo(() => {
    return mergeOverlappingLineSegments(
      rawSegments.map((s) => ({
        id: s.id,
        x1: s.x1 * sx,
        y1: s.y1 * sy,
        x2: s.x2 * sx,
        y2: s.y2 * sy,
        kind: s.kind ?? undefined,
        confidence: s.confidence ?? undefined,
      }))
    );
  }, [rawSegments, sx, sy]);

  const rows = useMemo(() => {
    const rawById = new Map<string, PnidNodeLineSegment>(rawSegments.map((s) => [s.id, s]));
    return distinctSegments.map((d) => {
      const cluster = d.source_segment_ids.map((id) => rawById.get(id)).filter(Boolean) as PnidNodeLineSegment[];
      const status = deriveDistinctStatus(cluster);
      const done = status === 'confirmed' || status === 'deleted';
      return { distinct: d, cluster, status, done };
    });
  }, [distinctSegments, rawSegments]);

  const doneCount = rows.reduce((n, r) => n + (r.done ? 1 : 0), 0);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Position: keep inside viewport
  const width = 420;
  const padding = 16;
  let left = position.x + 16;
  let top = position.y + 12;
  if (left + width > window.innerWidth - padding) left = position.x - width - 16;
  if (left < padding) left = padding;
  if (top + 360 > window.innerHeight - padding) top = Math.max(padding, window.innerHeight - 360 - padding);

  const headerLineNo = node.line_no || `Node #${node.node_no}`;
  const baseSubtitle = `${rows.length} distinct | ${rawSegments.length} raw | ${doneCount} done`;
  const scaleSubtitle =
    segmentCoordinateInfo
      ? ` | sx=${sx.toFixed(2)} sy=${sy.toFixed(2)}`
      : '';
  const dimsSubtitle =
    segmentCoordinateInfo?.source && segmentCoordinateInfo?.target
      ? ` | ${segmentCoordinateInfo.source_label ?? 'ai'} ${Math.round(segmentCoordinateInfo.source.width_px)}×${Math.round(segmentCoordinateInfo.source.height_px)} → img ${Math.round(segmentCoordinateInfo.target.width_px)}×${Math.round(segmentCoordinateInfo.target.height_px)}`
      : '';
  const subtitle = `${baseSubtitle}${scaleSubtitle}${dimsSubtitle}`;

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 1100,
        width,
        background: 'white',
        borderRadius: 12,
        boxShadow: '0 12px 40px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid #e5e7eb',
          background: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Review Segments · Node #{node.node_no}
          </div>
          <div style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {headerLineNo}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{subtitle}</div>
        </div>

        <button
          onClick={onClose}
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            border: '1px solid #e5e7eb',
            background: 'white',
            cursor: 'pointer',
            color: '#6b7280',
            fontSize: 14,
          }}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div style={{ maxHeight: 280, overflow: 'auto' }}>
        {rows.map(({ distinct, status }) => {
          const isHovered = hoveredDistinctId === distinct.id;
          const isSelected = selectedDistinctId === distinct.id;
          const st = statusStyle(status);
          return (
            <div
              key={distinct.id}
              onMouseEnter={() => onHoverDistinct(distinct.id)}
              onMouseLeave={() => onHoverDistinct(null)}
              onClick={() => onSelectDistinct(distinct.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderBottom: '1px solid #f3f4f6',
                cursor: 'pointer',
                background: isSelected ? 'rgba(59, 130, 246, 0.08)' : isHovered ? 'rgba(17, 24, 39, 0.03)' : 'white',
              }}
            >
              <div style={{ width: 78, flex: '0 0 auto' }}>
                <SegmentPreview seg={distinct} status={status} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>Segment</div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: 999,
                      background: st.bg,
                      color: st.color,
                      border: `1px solid ${st.border}`,
                    }}
                  >
                    {st.label}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>
                  {distinct.merged_count} raw · id {distinct.id}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onConfirmDistinct(distinct);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#10b981',
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  ✓
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteDistinct(distinct);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: '1px solid #fecaca',
                    background: '#fef2f2',
                    color: '#dc2626',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  🗑
                </button>
              </div>
            </div>
          );
        })}

        {rows.length === 0 && (
          <div style={{ padding: 14, fontSize: 12, color: '#6b7280' }}>No segments found for this node.</div>
        )}
      </div>

      <div
        style={{
          padding: 12,
          borderTop: '1px solid #e5e7eb',
          background: '#fafafa',
          display: 'flex',
          gap: 10,
          justifyContent: 'space-between',
        }}
      >
        <button
          onClick={onConfirmAll}
          style={{
            padding: '8px 12px',
            borderRadius: 10,
            border: 'none',
            background: '#2563eb',
            color: 'white',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Confirm All
        </button>
        <button
          onClick={onClose}
          style={{
            padding: '8px 12px',
            borderRadius: 10,
            border: '1px solid #d1d5db',
            background: 'white',
            color: '#374151',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default LineSegmentReviewPopover;
